import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Alert,
  IconButton,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  MenuItem,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Skeleton,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  ChecklistRtl as ChecklistIcon,
  LocationOn as LocationIcon,
  Upload as UploadIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { cantieriService, clientiService, interventiService, rapportiniService, presenzeService } from '../services/api';
import { generaRiepilogoCantierePDF, generaReportMensileCantierePDF, etichettaMese } from '../utils/pdf';
import { iniziali } from '../utils/formato';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cantiere-tabpanel-${index}`}
      aria-labelledby={`cantiere-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Componente StatBox per le statistiche
const StatBox = ({ icon: Icon, value, label, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      bgcolor: 'rgba(255,255,255,0.1)',
      borderRadius: 2,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        bgcolor: color || 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ color: 'white', fontSize: 20 }} />
    </Box>
    <Box>
      <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

// Componente per sezione form
const FormSection = ({ title, icon: Icon, children }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {Icon && <Icon sx={{ color: '#e50000', fontSize: 20 }} />}
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.75rem',
        }}
      >
        {title}
      </Typography>
    </Box>
    <Grid container spacing={2.5}>
      {children}
    </Grid>
  </Box>
);

const CantiereDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showExcelInfo, setShowExcelInfo] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingQuantita, setEditingQuantita] = useState(0);

  const [cantiere, setCantiere] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [interventi, setInterventi] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [rapportini, setRapportini] = useState({});
  const [selectedRapportino, setSelectedRapportino] = useState(null);
  const [viewRapportinoDialog, setViewRapportinoDialog] = useState(false);
  const [rapportinoPresenze, setRapportinoPresenze] = useState([]);

  // Stampa riepilogo rapportini per cantiere
  // --- Report mensile (tab 3) ---
  const [meseReport, setMeseReport] = useState(dayjs());
  const [reportRapportini, setReportRapportini] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportCaricato, setReportCaricato] = useState(false);

  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printDataInizio, setPrintDataInizio] = useState(null);
  const [printDataFine, setPrintDataFine] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [printError, setPrintError] = useState('');

  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    cliente_id: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    referente_cantiere: '',
    telefono_referente: '',
    email_referente: '',
    stato: 'attivo',
    data_inizio: null,
    data_fine_prevista: null,
    note: '',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [cantiereRes, clientiRes, interventiRes] = await Promise.all([
        cantieriService.getById(id),
        clientiService.getAll(),
        interventiService.getAll(),
      ]);

      setCantiere(cantiereRes.data);
      setFormData({
        ...cantiereRes.data,
        data_inizio: cantiereRes.data.data_inizio ? dayjs(cantiereRes.data.data_inizio) : null,
        data_fine_prevista: cantiereRes.data.data_fine_prevista ? dayjs(cantiereRes.data.data_fine_prevista) : null,
      });
      setClienti(clientiRes.data);

      const cantiereInterventi = interventiRes.data.filter(i => i.cantiere_id === parseInt(id));
      setInterventi(cantiereInterventi);

      const rapportiniMap = {};
      for (const intervento of cantiereInterventi) {
        try {
          const rapportinoRes = await rapportiniService.getByIntervento(intervento.id);
          if (rapportinoRes.data) {
            rapportiniMap[intervento.id] = rapportinoRes.data;
          }
        } catch (err) {
          // Nessun rapportino
        }
      }
      setRapportini(rapportiniMap);

      try {
        const tasksRes = await cantieriService.getTasks(id);
        setTasks(Array.isArray(tasksRes.data.tasks) ? tasksRes.data.tasks : []);
      } catch (err) {
        setTasks([]);
      }

    } catch (error) {
      setError('Errore nel caricamento dei dati');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
    // Il report si carica solo quando serve: è una chiamata in più che non ha
    // senso fare all'apertura della pagina.
    if (newValue === 3 && !reportCaricato) caricaReportMensile();
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSave = async () => {
    try {
      setError('');
      const dataToSend = {
        ...formData,
        data_inizio: formData.data_inizio ? formData.data_inizio.format('YYYY-MM-DD') : null,
        data_fine_prevista: formData.data_fine_prevista ? formData.data_fine_prevista.format('YYYY-MM-DD') : null,
      };
      await cantieriService.update(id, dataToSend);
      setSuccess('Cantiere aggiornato con successo');
      setEditing(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Il file deve essere in formato Excel (.xlsx o .xls)');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('replace', 'false');

    try {
      setUploading(true);
      setError('');
      setSuccess('');
      const response = await cantieriService.importTasks(id, formDataUpload);
      setSuccess(response.data.message || 'Task importate con successo');
      await loadData();
      event.target.value = '';
    } catch (err) {
      setError(err.response?.data?.error || 'Errore durante l\'import del file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await cantieriService.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_tasks.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Impossibile scaricare il template');
    }
  };

  const handleUpdateQuantitaCompletata = async (taskId, quantitaCompletata, quantitaTotale) => {
    try {
      const completata = parseFloat(quantitaCompletata) >= parseFloat(quantitaTotale);
      await cantieriService.updateTask(taskId, {
        quantita_completata: quantitaCompletata,
        completata: completata,
      });
      await loadData();
      setEditingTaskId(null);
      setEditingQuantita(0);
    } catch (err) {
      setError('Errore nell\'aggiornamento della task');
      setEditingTaskId(null);
      setEditingQuantita(0);
    }
  };

  const handleToggleTaskCompleted = async (task) => {
    try {
      const updateData = { completata: !task.completata };
      if (task.quantita && !task.completata) {
        updateData.quantita_completata = task.quantita;
      } else if (task.completata) {
        updateData.quantita_completata = 0;
      }
      await cantieriService.updateTask(task.id, updateData);
      await loadData();
    } catch (err) {
      setError('Errore nell\'aggiornamento della task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa task?')) return;
    try {
      await cantieriService.deleteTask(taskId);
      setSuccess('Task eliminata con successo');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Errore nell\'eliminazione della task');
    }
  };

  const getStatusColor = (stato) => {
    switch (stato) {
      case 'attivo':
        return { bg: '#dcfce7', color: '#166534' };
      case 'completato':
        return { bg: '#dbeafe', color: '#1e40af' };
      case 'in_corso':
        return { bg: '#fef3c7', color: '#92400e' };
      case 'programmato':
        return { bg: '#e0e7ff', color: '#3730a3' };
      case 'sospeso':
        return { bg: '#fef2f2', color: '#991b1b' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };


  const handleOpenPrintDialog = () => {
    setPrintError('');
    setPrintDataInizio(null);
    setPrintDataFine(null);
    setPrintDialogOpen(true);
  };

  // Carica i rapportini del mese scelto per questo cantiere. L'endpoint
  // /rapportini/cantiere/:id copre sia i rapportini legati a un intervento sia
  // quelli agganciati direttamente al cantiere.
  const caricaReportMensile = useCallback(
    async (mese) => {
      if (!id) return;
      const m = mese || meseReport;
      if (!m) return;
      try {
        setReportLoading(true);
        setReportError('');
        const res = await rapportiniService.getByCantiere(id, {
          data_inizio: m.startOf('month').format('YYYY-MM-DD'),
          data_fine: m.endOf('month').format('YYYY-MM-DD'),
        });
        setReportRapportini(Array.isArray(res.data) ? res.data : []);
        setReportCaricato(true);
      } catch (err) {
        console.error('Errore nel caricamento del report mensile:', err);
        setReportError(
          err.isOffline
            ? 'Sei offline: il report non può essere aggiornato.'
            : 'Non è stato possibile caricare il report del mese.'
        );
      } finally {
        setReportLoading(false);
      }
    },
    [id, meseReport]
  );

  // Ore per persona e per giorno. Se la stessa persona ha più rapportini nello
  // stesso giorno (due interventi sullo stesso cantiere) le ore si sommano in
  // un'unica riga, come chiede un foglio ore.
  const reportPersone = useMemo(() => {
    const perPersona = new Map();
    reportRapportini.forEach((r) => {
      const ore = Array.isArray(r.ore_per_tecnico) ? r.ore_per_tecnico : [];
      ore.forEach((t) => {
        if (!t || t.user_id === undefined || t.user_id === null) return;
        if (!perPersona.has(t.user_id)) {
          perPersona.set(t.user_id, { user_id: t.user_id, nome: t.nome || '—', perGiorno: new Map() });
        }
        const persona = perPersona.get(t.user_id);
        const giorno = persona.perGiorno.get(r.data) || { data: r.data, ore: 0, note: [] };
        giorno.ore += Number(t.ore) || 0;
        if (t.entrata) giorno.note.push(`entrata ${String(t.entrata).slice(0, 5)}`);
        if (t.uscita_anticipata) giorno.note.push(`uscita ${String(t.uscita_anticipata).slice(0, 5)}`);
        persona.perGiorno.set(r.data, giorno);
      });
    });

    return [...perPersona.values()]
      .map((p) => {
        const giorni = [...p.perGiorno.values()]
          .sort((a, b) => String(a.data).localeCompare(String(b.data)))
          .map((g) => ({ ...g, note: [...new Set(g.note)].join(' · ') }));
        return {
          ...p,
          giorni,
          giorniLavorati: giorni.length,
          totaleOre: giorni.reduce((acc, g) => acc + g.ore, 0),
        };
      })
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [reportRapportini]);

  const reportTotaleOre = useMemo(
    () => reportPersone.reduce((acc, p) => acc + p.totaleOre, 0),
    [reportPersone]
  );

  // Rapportini del periodo che non riportano le ore per persona: senza segnalarli
  // il totale sembrerebbe completo pur non essendolo.
  const reportSenzaOre = useMemo(
    () =>
      reportRapportini.filter(
        (r) => !Array.isArray(r.ore_per_tecnico) || r.ore_per_tecnico.length === 0
      ).length,
    [reportRapportini]
  );

  const handleScaricaReportPDF = () => {
    // Se il mese digitato a mano non è valido non si genera niente: altrimenti
    // uscirebbe un PDF intitolato "Invalid Date".
    if (!meseReport || !meseReport.isValid()) return;
    const clienteCantiere = clienti.find((cl) => cl.id === cantiere?.cliente_id);
    const cliente_nome =
      cantiere?.cliente_nome ||
      cantiere?.cliente?.ragione_sociale ||
      (typeof cantiere?.cliente === 'string' ? cantiere.cliente : '') ||
      clienteCantiere?.ragione_sociale ||
      '';
    generaReportMensileCantierePDF(
      { ...cantiere, cliente_nome },
      meseReport.format('YYYY-MM'),
      reportPersone,
      { rapportiniSenzaOre: reportSenzaOre }
    );
  };

  const handleGeneraRiepilogoPDF = async () => {
    if (!cantiere) return;
    setPrintError('');
    setPrintLoading(true);
    try {
      const dataInizio = printDataInizio ? printDataInizio.format('YYYY-MM-DD') : null;
      const dataFine = printDataFine ? printDataFine.format('YYYY-MM-DD') : null;

      const params = {};
      if (dataInizio) params.data_inizio = dataInizio;
      if (dataFine) params.data_fine = dataFine;

      const res = await rapportiniService.getByCantiere(cantiere.id, params);
      const lista = Array.isArray(res.data) ? res.data : [];

      if (lista.length === 0) {
        setPrintError('Nessun rapportino per il periodo selezionato');
        return;
      }

      // Risolvi il nome cliente: usa cliente_nome se presente, altrimenti
      // ricava la ragione sociale dall'elenco clienti.
      const clienteCantiere = clienti.find((cl) => cl.id === cantiere.cliente_id);
      const cliente_nome =
        cantiere.cliente_nome ||
        cantiere.cliente?.ragione_sociale ||
        (typeof cantiere.cliente === 'string' ? cantiere.cliente : '') ||
        clienteCantiere?.ragione_sociale ||
        '';

      const cantierePerPdf = { ...cantiere, cliente_nome };

      generaRiepilogoCantierePDF(cantierePerPdf, lista, { dataInizio, dataFine });
      setPrintDialogOpen(false);
    } catch (err) {
      console.error(err);
      setPrintError(err.response?.data?.error || err.response?.data?.message || 'Errore nella generazione del PDF');
    } finally {
      setPrintLoading(false);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Box sx={{ bgcolor: '#1a1a1a', pt: 4, pb: 12, px: 3 }}>
          <Container maxWidth="lg">
            <Skeleton variant="text" width={100} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={64} height={64} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
              <Box>
                <Skeleton variant="text" width={250} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                <Skeleton variant="text" width={180} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Box>
            </Box>
          </Container>
        </Box>
        <Container maxWidth="lg" sx={{ mt: -8 }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!cantiere) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <BuildIcon sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Cantiere non trovato
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/cantieri')}
            sx={{
              mt: 2,
              bgcolor: '#e50000',
              '&:hover': { bgcolor: '#c50000' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Torna ai Cantieri
          </Button>
        </Paper>
      </Box>
    );
  }

  const cliente = clienti.find(c => c.id === cantiere.cliente_id);
  const statusColor = getStatusColor(cantiere.stato);
  const interventiCompletati = interventi.filter(i => i.stato === 'completato').length;
  const tasksCompletate = tasks.filter(t => t.completata).length;

  // Calcolo progresso tasks
  let progressoTasks = 0;
  if (tasks.length > 0) {
    let totale = 0, completato = 0;
    tasks.forEach(task => {
      const q = task.quantita || 1;
      const qc = task.quantita_completata || (task.completata ? q : 0);
      totale += q;
      completato += qc;
    });
    progressoTasks = totale > 0 ? (completato / totale) * 100 : 0;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Header scuro */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          pt: { xs: 3, sm: 4 },
          pb: { xs: 14, sm: 16 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso */}
        <Box
          sx={{
            position: 'absolute',
            top: -100, right: -100,
            width: 300, height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(229,0,0,0.3) 0%, transparent 70%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Back button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/cantieri')}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              mb: 3,
              textTransform: 'none',
              '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Torna ai Cantieri
          </Button>

          {/* Info cantiere */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Avatar
              sx={{
                width: 72, height: 72,
                bgcolor: '#e50000',
                fontSize: '1.5rem',
                fontWeight: 800,
              }}
            >
              {iniziali(cantiere.nome)}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    px: 1.5, py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  {cantiere.codice}
                </Typography>
                <Chip
                  label={cantiere.stato}
                  size="small"
                  sx={{
                    bgcolor: statusColor.bg,
                    color: statusColor.color,
                    fontWeight: 700,
                    textTransform: 'capitalize',
                  }}
                />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  mb: 0.5,
                }}
              >
                {cantiere.nome}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                {/* minWidth 0: senza, il riquadro non poteva restringersi sotto la
                    larghezza del testo e la ragione sociale usciva dall'intestazione
                    (24px tagliati su telefono). */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 0 }}>
                    {cliente?.ragione_sociale || 'N/D'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {cantiere.citta}{cantiere.provincia ? ` (${cantiere.provincia})` : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Azioni header (desktop) */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Stampa riepilogo rapportini (sempre visibile) */}
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleOpenPrintDialog}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.4)',
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderColor: 'rgba(255,255,255,0.6)',
                  },
                }}
              >
                Stampa riepilogo
              </Button>

              {/* Pulsante modifica (desktop) */}
              {!isMobile && tabValue === 0 && (
                <Button
                  variant={editing ? 'outlined' : 'contained'}
                  startIcon={editing ? <CloseIcon /> : <EditIcon />}
                  onClick={() => setEditing(!editing)}
                  sx={{
                    bgcolor: editing ? 'transparent' : '#e50000',
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.3)',
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: editing ? 'rgba(255,255,255,0.1)' : '#c50000',
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                  }}
                >
                  {editing ? 'Annulla' : 'Modifica'}
                </Button>
              )}
            </Box>
          </Box>

          {/* Statistiche */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <StatBox icon={AssignmentIcon} value={interventi.length} label="Interventi" color="rgba(59,130,246,0.3)" />
            <StatBox icon={CheckCircleIcon} value={interventiCompletati} label="Completati" color="rgba(16,185,129,0.3)" />
            <StatBox icon={ChecklistIcon} value={tasks.length} label="Tasks" color="rgba(245,158,11,0.3)" />
            <StatBox icon={ScheduleIcon} value={`${progressoTasks.toFixed(0)}%`} label="Progresso" color="rgba(139,92,246,0.3)" />
          </Box>
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="lg" sx={{ mt: { xs: -10, sm: -12 }, pb: 4, position: 'relative', zIndex: 2 }}>
        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              // Con quattro schede, 'fullWidth' su 390px ne lascia ~97px l'una e le
              // etichette si accavallano sull'icona: meglio scorrevoli.
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons={isMobile ? 'auto' : false}
              allowScrollButtonsMobile
              sx={{
                px: { xs: 0, sm: 2 },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: 56,
                  color: '#6b7280',
                  '&.Mui-selected': { color: '#e50000' },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#e50000',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab icon={<BuildIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Anagrafica" />
              <Tab icon={<AssignmentIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={`Interventi (${interventi.length})`} />
              <Tab icon={<ChecklistIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={`Tasks (${tasks.length})`} />
              <Tab icon={<AssessmentIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Report mensile" />
            </Tabs>
          </Box>

          {/* Tab Anagrafica */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
              <FormSection title="Dati Principali">
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="codice"
                    label="Codice Cantiere"
                    fullWidth
                    required
                    value={formData.codice}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    name="nome"
                    label="Nome Cantiere"
                    fullWidth
                    required
                    value={formData.nome}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="cliente_id"
                    label="Cliente"
                    fullWidth
                    required
                    select
                    value={formData.cliente_id}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    {clienti.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.ragione_sociale}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="stato"
                    label="Stato"
                    fullWidth
                    required
                    select
                    value={formData.stato}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="attivo">Attivo</MenuItem>
                    <MenuItem value="completato">Completato</MenuItem>
                    <MenuItem value="sospeso">Sospeso</MenuItem>
                  </TextField>
                </Grid>
              </FormSection>

              <FormSection title="Ubicazione" icon={LocationIcon}>
                <Grid item xs={12}>
                  <TextField
                    name="indirizzo"
                    label="Indirizzo"
                    fullWidth
                    required
                    value={formData.indirizzo}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    name="citta"
                    label="Città"
                    fullWidth
                    required
                    value={formData.citta}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    name="cap"
                    label="CAP"
                    fullWidth
                    required
                    value={formData.cap}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    name="provincia"
                    label="Provincia"
                    fullWidth
                    required
                    value={formData.provincia}
                    onChange={handleInputChange}
                    disabled={!editing}
                    inputProps={{ maxLength: 2 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </FormSection>

              <FormSection title="Date" icon={CalendarIcon}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Data Inizio"
                    value={formData.data_inizio}
                    onChange={(value) => handleDateChange('data_inizio', value)}
                    disabled={!editing}
                    slotProps={{
                      textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Data Fine Prevista"
                    value={formData.data_fine_prevista}
                    onChange={(value) => handleDateChange('data_fine_prevista', value)}
                    disabled={!editing}
                    slotProps={{
                      textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } },
                    }}
                  />
                </Grid>
              </FormSection>

              <FormSection title="Referente Cantiere" icon={PersonIcon}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="referente_cantiere"
                    label="Nome Referente"
                    fullWidth
                    value={formData.referente_cantiere}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="telefono_referente"
                    label="Telefono Referente"
                    fullWidth
                    value={formData.telefono_referente}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="email_referente"
                    label="Email Referente"
                    fullWidth
                    type="email"
                    value={formData.email_referente}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </FormSection>

              <FormSection title="Note">
                <Grid item xs={12}>
                  <TextField
                    name="note"
                    label="Note"
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.note}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </FormSection>

              {editing && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        ...cantiere,
                        data_inizio: cantiere.data_inizio ? dayjs(cantiere.data_inizio) : null,
                        data_fine_prevista: cantiere.data_fine_prevista ? dayjs(cantiere.data_fine_prevista) : null,
                      });
                    }}
                    sx={{ color: '#6b7280', px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Annulla
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{
                      bgcolor: '#e50000', px: 3, borderRadius: 2,
                      textTransform: 'none', fontWeight: 600,
                      '&:hover': { bgcolor: '#c50000' },
                    }}
                  >
                    Salva Modifiche
                  </Button>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Tab Interventi */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {interventi.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <AssignmentIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="text.secondary">
                    Nessun intervento
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Non ci sono ancora interventi per questo cantiere
                  </Typography>
                </Box>
              ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {interventi.map((intervento) => {
                    const intStatusColor = getStatusColor(intervento.stato);
                    return (
                      <Paper
                        key={intervento.id}
                        elevation={0}
                        onClick={() => navigate(`/interventi/${intervento.id}`)}
                        sx={{
                          p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                          cursor: 'pointer', transition: 'all 0.2s ease',
                          '&:hover': { borderColor: '#e50000', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarIcon sx={{ fontSize: 16, color: '#e50000' }} />
                            <Typography variant="body2" fontWeight={700} color="#e50000">
                              {dayjs(intervento.data).format('DD MMM YYYY')}
                            </Typography>
                          </Box>
                          <Chip label={intervento.stato} size="small" sx={{ bgcolor: intStatusColor.bg, color: intStatusColor.color, fontWeight: 600, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                        </Box>
                        <Typography variant="body2"><strong>Orario:</strong> {intervento.ora_inizio} - {intervento.ora_fine}</Typography>
                        <Typography variant="body2"><strong>Tipo:</strong> {intervento.tipologia}</Typography>
                        <Typography variant="body2"><strong>Capocantiere:</strong> {intervento.capocantiere}</Typography>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Data</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Orario</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Tipologia</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Capocantiere</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Stato</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }} align="center">Rapportino</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interventi.map((intervento) => {
                        const intStatusColor = getStatusColor(intervento.stato);
                        return (
                          <TableRow key={intervento.id} sx={{ '&:hover': { bgcolor: '#fafafa' }, transition: 'background-color 0.2s' }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 16, color: '#e50000' }} />
                                <Typography variant="body2" fontWeight={600}>
                                  {dayjs(intervento.data).format('DD/MM/YYYY')}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell><Typography variant="body2">{intervento.ora_inizio} - {intervento.ora_fine}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{intervento.tipologia}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{intervento.capocantiere}</Typography></TableCell>
                            <TableCell>
                              <Chip label={intervento.stato} size="small" sx={{ bgcolor: intStatusColor.bg, color: intStatusColor.color, fontWeight: 600, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell align="center">
                              {rapportini[intervento.id] ? (
                                <Tooltip title="Visualizza Rapportino">
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      setSelectedRapportino(rapportini[intervento.id]);
                                      try {
                                        const presenzeRes = await presenzeService.getByIntervento(intervento.id);
                                        setRapportinoPresenze(presenzeRes.data);
                                      } catch (err) {
                                        setRapportinoPresenze([]);
                                      }
                                      setViewRapportinoDialog(true);
                                    }}
                                    sx={{ color: '#10b981', '&:hover': { bgcolor: '#ecfdf5' } }}
                                  >
                                    <DescriptionIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Chip label="Non compilato" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>

          {/* Tab Tasks */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Info Excel */}
              <Alert
                severity="info"
                sx={{ mb: 3, borderRadius: 2 }}
                action={
                  <IconButton size="small" onClick={() => setShowExcelInfo(!showExcelInfo)} sx={{ color: 'inherit' }}>
                    <InfoIcon />
                  </IconButton>
                }
              >
                <Typography variant="body2" fontWeight={600}>Importa task da file Excel</Typography>
                <Collapse in={showExcelInfo}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Il file Excel deve contenere le colonne:</Typography>
                    <List dense>
                      <ListItem sx={{ py: 0.25 }}><ListItemText primary="• Descrizione" secondary="(obbligatorio)" /></ListItem>
                      <ListItem sx={{ py: 0.25 }}><ListItemText primary="• Quantità" secondary="(opzionale)" /></ListItem>
                      <ListItem sx={{ py: 0.25 }}><ListItemText primary="• Note" secondary="(opzionale)" /></ListItem>
                    </List>
                  </Box>
                </Collapse>
              </Alert>

              {/* Header e pulsanti */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                  Elenco Task ({tasks.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Scarica Template
                  </Button>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                    disabled={uploading}
                    size="small"
                    sx={{ bgcolor: '#e50000', '&:hover': { bgcolor: '#c50000' }, borderRadius: 2, textTransform: 'none' }}
                  >
                    {uploading ? 'Caricamento...' : 'Importa Excel'}
                    <input type="file" hidden accept=".xlsx, .xls" onChange={handleFileUpload} />
                  </Button>
                </Box>
              </Box>

              {/* Barra progresso */}
              {tasks.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3, mb: 3, borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(229,0,0,0.05) 0%, rgba(229,0,0,0.02) 100%)',
                    border: '1px solid rgba(229,0,0,0.1)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Progresso Complessivo</Typography>
                    <Typography variant="h5" fontWeight={800} color={progressoTasks === 100 ? '#10b981' : '#e50000'}>
                      {progressoTasks.toFixed(0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progressoTasks}
                    sx={{
                      height: 12, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: progressoTasks === 100 ? '#10b981' : '#e50000',
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 4, mt: 2, justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Completate</Typography>
                      <Typography variant="h6" fontWeight={700}>{tasksCompletate} / {tasks.length}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Rimanenti</Typography>
                      <Typography variant="h6" fontWeight={700} color="#e50000">{tasks.length - tasksCompletate}</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* Lista Tasks */}
              {tasks.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <ChecklistIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="text.secondary">Nessuna task</Typography>
                  <Typography variant="body2" color="text.secondary">Carica un file Excel per importare le task</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {tasks.map((task, index) => {
                    const qc = task.quantita_completata || 0;
                    const qt = task.quantita || 0;
                    const progress = qt > 0 ? Math.min((qc / qt) * 100, 100) : (task.completata ? 100 : 0);

                    return (
                      <Paper
                        key={task.id || index}
                        elevation={0}
                        sx={{
                          p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                          bgcolor: task.completata ? 'rgba(16,185,129,0.05)' : 'white',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{ textDecoration: task.completata ? 'line-through' : 'none', color: task.completata ? '#6b7280' : 'inherit' }}
                            >
                              {task.descrizione}
                            </Typography>
                            {task.note && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {task.note}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={task.completata ? 'Completata' : progress > 0 ? 'In corso' : 'Da fare'}
                              size="small"
                              sx={{
                                bgcolor: task.completata ? '#dcfce7' : progress > 0 ? '#fef3c7' : '#f3f4f6',
                                color: task.completata ? '#166534' : progress > 0 ? '#92400e' : '#6b7280',
                                fontWeight: 600, fontSize: '0.7rem',
                              }}
                            />
                            <Tooltip title="Elimina task">
                              <IconButton size="small" onClick={() => handleDeleteTask(task.id)} sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {qt > 0 ? (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                              <TextField
                                type="number"
                                label="Completata"
                                size="small"
                                value={editingTaskId === task.id ? editingQuantita : qc}
                                onFocus={() => { setEditingTaskId(task.id); setEditingQuantita(qc); }}
                                onChange={(e) => setEditingQuantita(Math.max(0, Math.min(parseFloat(e.target.value) || 0, qt)))}
                                inputProps={{ min: 0, max: qt, step: 1 }}
                                sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                              <Typography variant="body2" color="text.secondary">/ {qt}</Typography>
                              {editingTaskId === task.id && editingQuantita !== qc && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleUpdateQuantitaCompletata(task.id, editingQuantita, qt)}
                                  sx={{ bgcolor: '#e50000', '&:hover': { bgcolor: '#c50000' }, borderRadius: 2, textTransform: 'none' }}
                                >
                                  Salva
                                </Button>
                              )}
                              <Typography variant="body2" fontWeight={700} color={progress === 100 ? '#10b981' : '#e50000'}>
                                {progress.toFixed(0)}%
                              </Typography>
                              <Tooltip title={task.completata ? 'Resetta' : 'Completa tutto'}>
                                <IconButton size="small" onClick={() => handleToggleTaskCompleted(task)} sx={{ color: task.completata ? '#10b981' : '#9ca3af' }}>
                                  {task.completata ? <CheckCircleIcon /> : <UncheckedIcon />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: 6, borderRadius: 1, bgcolor: '#e5e7eb',
                                '& .MuiLinearProgress-bar': { bgcolor: task.completata ? '#10b981' : '#e50000', borderRadius: 1 },
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title={task.completata ? 'Marca da fare' : 'Marca completata'}>
                              <IconButton size="small" onClick={() => handleToggleTaskCompleted(task)} sx={{ color: task.completata ? '#10b981' : '#9ca3af' }}>
                                {task.completata ? <CheckCircleIcon /> : <UncheckedIcon />}
                              </IconButton>
                            </Tooltip>
                            <Typography variant="body2" color="text.secondary">
                              {task.completata ? 'Completata' : 'Clicca per completare'}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Tab Report mensile: ore per persona, giorno per giorno */}
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <DatePicker
                  label="Mese"
                  views={['year', 'month']}
                  openTo="month"
                  value={meseReport}
                  onChange={(nuovo) => {
                    setMeseReport(nuovo);
                    if (nuovo && nuovo.isValid()) caricaReportMensile(nuovo);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: { sm: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleScaricaReportPDF}
                  disabled={reportLoading || reportPersone.length === 0 || !meseReport?.isValid()}
                  sx={{
                    bgcolor: '#e50000',
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#c50000' },
                  }}
                >
                  Scarica PDF
                </Button>
              </Box>

              {reportError && (
                <Alert
                  severity="error"
                  sx={{ mb: 3, borderRadius: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => caricaReportMensile()}>
                      Riprova
                    </Button>
                  }
                >
                  {reportError}
                </Alert>
              )}

              {reportLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#e50000' }} />
                </Box>
              ) : reportPersone.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <AssessmentIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="text.secondary">
                    Nessuna ora registrata
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {meseReport?.isValid()
                      ? `Su questo cantiere non risultano ore in ${etichettaMese(meseReport.format('YYYY-MM'))}.`
                      : 'Seleziona un mese valido.'}
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Riepilogo in cima: è il numero che si cerca per primo */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: 'rgba(229, 0, 0, 0.05)',
                      border: '1px solid rgba(229, 0, 0, 0.2)',
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Ore totali</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#e50000' }}>
                            {reportTotaleOre.toFixed(1)} h
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Persone</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                            {reportPersone.length}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Giornate</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                            {reportPersone.reduce((a, p) => a + p.giorniLavorati, 0)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {reportSenzaOre > 0 && (
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                      {reportSenzaOre} rapportino/i del mese non riporta le ore per persona:
                      non è conteggiato nei totali qui sopra.
                    </Alert>
                  )}

                  {/* Una sezione per persona, con una riga per ogni giornata */}
                  {reportPersone.map((persona) => (
                    <Paper
                      key={persona.user_id}
                      elevation={0}
                      sx={{
                        mb: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          bgcolor: '#fafafa',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#1a1a1a', fontSize: '0.75rem', fontWeight: 700 }}>
                          {iniziali(persona.nome)}
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                          {persona.nome}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${persona.giorniLavorati} ${persona.giorniLavorati === 1 ? 'giornata' : 'giornate'}`}
                          sx={{ bgcolor: '#f3f4f6', color: '#4b5563', fontWeight: 600 }}
                        />
                        <Chip
                          size="small"
                          label={`${persona.totaleOre.toFixed(1)} h`}
                          sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700 }}
                        />
                      </Box>
                      <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Giorno</TableCell>
                              <TableCell align="right">Ore</TableCell>
                              {!isMobile && <TableCell>Note</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {persona.giorni.map((g) => (
                              <TableRow key={g.data} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {dayjs(g.data).format('DD/MM/YYYY')}
                                </TableCell>
                                <TableCell sx={{ textTransform: 'capitalize' }}>
                                  {dayjs(g.data).locale('it').format('dddd')}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {g.ore.toFixed(1)} h
                                </TableCell>
                                {!isMobile && (
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {g.note || '—'}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                              <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Totale</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {persona.totaleOre.toFixed(1)} h
                              </TableCell>
                              {!isMobile && <TableCell />}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  ))}
                </>
              )}
            </Box>
          </TabPanel>
        </Paper>
      </Container>

      {/* FAB Mobile */}
      {isMobile && tabValue === 0 && (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
          {editing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{ bgcolor: '#e50000', borderRadius: 28, px: 3, py: 1.5, boxShadow: '0 4px 14px rgba(229,0,0,0.4)', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#c50000' } }}
              >
                Salva
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditing(false);
                  setFormData({ ...cantiere, data_inizio: cantiere.data_inizio ? dayjs(cantiere.data_inizio) : null, data_fine_prevista: cantiere.data_fine_prevista ? dayjs(cantiere.data_fine_prevista) : null });
                }}
                sx={{ borderRadius: 28, px: 3, py: 1.5, bgcolor: 'white', borderColor: '#e5e7eb', color: '#6b7280', textTransform: 'none', fontWeight: 600, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
              >
                Annulla
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
              sx={{ bgcolor: '#e50000', borderRadius: 28, px: 3, py: 1.5, boxShadow: '0 4px 14px rgba(229,0,0,0.4)', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#c50000' } }}
            >
              Modifica
            </Button>
          )}
        </Box>
      )}

      {/* Dialog Rapportino */}
      <Dialog open={viewRapportinoDialog} onClose={() => setViewRapportinoDialog(false)} maxWidth="md" fullWidth TransitionComponent={Fade} PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: '1px solid', borderColor: 'divider' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DescriptionIcon sx={{ color: '#166534' }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>Rapportino di Lavoro</Typography>
          </Box>
          <IconButton onClick={() => setViewRapportinoDialog(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedRapportino && (
            <>
              {/* Info */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#fafafa', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Cliente</Typography><Typography variant="body2" fontWeight={600}>{selectedRapportino.cliente_nome}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Data</Typography><Typography variant="body2" fontWeight={600}>{selectedRapportino.data ? dayjs(selectedRapportino.data).format('DD/MM/YYYY') : '-'}</Typography></Grid>
                </Grid>
              </Paper>

              {/* Orari */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><AccessTimeIcon sx={{ fontSize: 18 }} />Orari e Tempi</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <Table size="small">
                  <TableBody>
                    {selectedRapportino.ora_partenza_sede && <TableRow><TableCell>Partenza Sede</TableCell><TableCell align="right">{selectedRapportino.ora_partenza_sede.substring(0, 5)}</TableCell></TableRow>}
                    {(selectedRapportino.ora_arrivo_cantiere || selectedRapportino.ora_inizio_lavoro) && <TableRow><TableCell>Arrivo Cantiere / Inizio Lavoro</TableCell><TableCell align="right">{(selectedRapportino.ora_arrivo_cantiere || selectedRapportino.ora_inizio_lavoro).substring(0, 5)}</TableCell></TableRow>}
                    {selectedRapportino.ora_fine_lavoro && <TableRow><TableCell>Fine Lavoro</TableCell><TableCell align="right">{selectedRapportino.ora_fine_lavoro.substring(0, 5)}</TableCell></TableRow>}
                    <TableRow><TableCell><strong>Ore Lavorate</strong></TableCell><TableCell align="right"><strong>{selectedRapportino.ore_lavorate?.toFixed(2) || 0} ore</strong></TableCell></TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Team */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><GroupIcon sx={{ fontSize: 18 }} />Team</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: '#dcfce7', borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="#166534">Presenti</Typography>
                    <Box sx={{ mt: 1 }}>
                      {rapportinoPresenze.filter(p => p.presente).map((p, idx) => (
                        <Chip key={idx} label={p.user_nome} size="small" sx={{ mr: 0.5, mb: 0.5, bgcolor: 'white' }} />
                      ))}
                      {rapportinoPresenze.filter(p => p.presente).length === 0 && <Typography variant="body2">Nessuno</Typography>}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="#991b1b">Assenti</Typography>
                    <Box sx={{ mt: 1 }}>
                      {rapportinoPresenze.filter(p => !p.presente).map((p, idx) => (
                        <Chip key={idx} label={`${p.user_nome}: ${p.motivo_assenza}`} size="small" sx={{ mr: 0.5, mb: 0.5, bgcolor: 'white' }} />
                      ))}
                      {rapportinoPresenze.filter(p => !p.presente).length === 0 && <Typography variant="body2">Nessuno</Typography>}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Descrizione */}
              {selectedRapportino.descrizione_intervento && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Descrizione Lavori</Typography>
                  <Paper sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRapportino.descrizione_intervento}</Typography>
                  </Paper>
                </Box>
              )}

              {/* Note */}
              {selectedRapportino.note && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Note</Typography>
                  <Paper sx={{ p: 2, bgcolor: '#fef3c7', borderRadius: 2, border: '1px solid #fbbf24' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRapportino.note}</Typography>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setViewRapportinoDialog(false)} variant="contained" sx={{ bgcolor: '#e50000', '&:hover': { bgcolor: '#c50000' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Stampa riepilogo rapportini */}
      <Dialog
        open={printDialogOpen}
        onClose={() => { if (!printLoading) setPrintDialogOpen(false); }}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: '1px solid', borderColor: 'divider' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(229,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PictureAsPdfIcon sx={{ color: '#e50000' }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>Stampa riepilogo</Typography>
          </Box>
          <IconButton onClick={() => setPrintDialogOpen(false)} size="small" disabled={printLoading}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, mt: 1 }}>
            Seleziona un intervallo di date oppure lascia i campi vuoti per includere tutto lo storico dei rapportini di questo cantiere.
          </Typography>
          {printError && (
            <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setPrintError('')}>
              {printError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Da"
                value={printDataInizio}
                onChange={(value) => setPrintDataInizio(value)}
                maxDate={printDataFine || undefined}
                disabled={printLoading}
                slotProps={{
                  textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } },
                  field: { clearable: true, onClear: () => setPrintDataInizio(null) },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="A"
                value={printDataFine}
                onChange={(value) => setPrintDataFine(value)}
                minDate={printDataInizio || undefined}
                disabled={printLoading}
                slotProps={{
                  textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } },
                  field: { clearable: true, onClear: () => setPrintDataFine(null) },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => setPrintDialogOpen(false)}
            disabled={printLoading}
            sx={{ color: '#6b7280', px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleGeneraRiepilogoPDF}
            variant="contained"
            disabled={printLoading}
            startIcon={printLoading ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />}
            sx={{ bgcolor: '#e50000', '&:hover': { bgcolor: '#c50000' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {printLoading ? 'Generazione…' : 'Genera PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CantiereDettaglio;
